import { useState, useEffect, useRef } from 'react';
import { RotterdamForm } from './rotterdam-form.jsx';
import { PESTS, STEDEN } from './data.js';
import { PestDrawing, IconPhone, IconPin, IconCheck, IconStar, IconShield, IconBolt, IconLeaf, IconWallet, IconMail } from './icons.jsx';
import { useSEO, buildStadSchema, buildBreadcrumb } from './seo.jsx';
import { url } from './url.js';
import { REVIEWS, GOOGLE_REVIEWS_URL } from './data.js';
import { getGoogleReviews, getGoogleAggregate, GOOGLE_REVIEW_FALLBACK, GOOGLE_REVIEWS_FALLBACK } from './google-reviews.js';
import { RotterdamExtras } from './rotterdam-extra.jsx';
import { DenHaagExtras } from './den-haag-extra.jsx';
import { CityExtras, CITY_CONTENT } from './city-extras.jsx';
import { TrustLuxe } from './trust-luxe.jsx';
import { RotterdamHero } from './rotterdam-hero.jsx';
import { StadWijkenLuxe } from './rotterdam-wijken.jsx';
import { STAD_WIJKEN_PER_SLUG } from './stad-wijken-data.js';
import { RotterdamChase } from './rotterdam-chase.jsx';
import { AmsterdamHero } from './amsterdam-hero.jsx';
import { DenHaagHero } from './den-haag-hero.jsx';
import { UtrechtHero } from './utrecht-hero.jsx';
import { PlaagCard } from './andere-diensten-carousel.jsx';

// Per-city bespoke hero. Slugs not in this map fall back to the generic
// stad-hero template defined below in StadPage's JSX.
// (Den Haag removed 2026-05-25 + Amsterdam/Utrecht removed 2026-05-26,
//  gebruikersfeedback: alle stad-pagina's moeten visueel gelijk zijn voor
//  consistente UX. De bespoke hero-componenten blijven bestaan voor evt.
//  toekomstig gebruik maar worden nergens meer aangeroepen.)
const CITY_HEROES = {};

// Auto-load Rotterdam luxe stylesheet once, when a Rotterdam page mounts.
function ensureRotterdamLuxeCss() {
  if (typeof document === 'undefined') return;
  if (document.querySelector('link[data-rdam-luxe]')) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'rotterdam-luxe.css';
  link.setAttribute('data-rdam-luxe', '1');
  document.head.appendChild(link);
}

// Scroll progress bar — Rotterdam-only premium touch.
function useScrollProgress(enabled) {
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    let bar = document.querySelector('.rdam-progress');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'rdam-progress';
      document.body.prepend(bar);
    }
    const onScroll = () => {
      const max = document.body.scrollHeight - window.innerHeight;
      const pct = max > 0 ? (window.scrollY / max) * 100 : 0;
      bar.style.width = pct + '%';
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      bar && bar.remove();
    };
  }, [enabled]);
}

// Wijken / surrounding cities per stad — used in werkgebied section.
// Single source of truth zit nu in src/stad-wijken-data.js zodat zowel
// stad.jsx als StadWijkenLuxe denzelfde data lezen. Alias hieronder
// houdt bestaande code (FAQs, wijken-pills section) ongewijzigd werkend.
const STAD_WIJKEN = STAD_WIJKEN_PER_SLUG;
// Oude inline literal (voor referentie, niet gebruikt):

// Lookup nearby cities (same province) — for werkgebied section
function nearbyCities(stad) {
  return STEDEN
    .filter(s => s.provincie === stad.provincie && s.slug !== stad.slug)
    .slice(0, 6);
}

// Deterministisch unieke hero-subkop per stad — voorkomt duplicate content
// op de 81 generic stadpagina's. Layout en styling blijven identiek; alleen
// de woorden variëren tussen 4 templates op basis van slug-hash zodat
// dezelfde stad altijd dezelfde tekst behoudt tussen builds.
function uniqueStadIntro(stad) {
  const variants = [
    `Onze gecertificeerde specialisten zijn dagelijks actief in ${stad.naam} en omliggende gemeenten in ${stad.provincie}. Gemiddeld binnen 24 uur op locatie, vaste prijs vooraf.`,
    `In ${stad.naam} bestrijden wij wespen, muizen, ratten en ander ongedierte met een vakkundige, structurele aanpak. Spoeddienst beschikbaar 7 dagen per week in heel ${stad.provincie}.`,
    `Last van ongedierte in ${stad.naam}? Onze bestrijders kennen het werkgebied en pakken het snel, vakkundig en milieuverantwoord aan. Reactie binnen 24 uur.`,
    `Voor inwoners en bedrijven in ${stad.naam} en heel ${stad.provincie}: ABC is dichtbij, snel ter plekke en werkt met CTGB-gecertificeerde middelen en een vakkundige nacontrole.`,
  ];
  let h = 0;
  for (let i = 0; i < stad.slug.length; i++) h = (h * 31 + stad.slug.charCodeAt(i)) >>> 0;
  return variants[h % variants.length];
}

function FaqItem({ q, a, open, onToggle }) {
  return (
    <div className={"faq-item" + (open ? " open" : "")}>
      <button className="faq-question" onClick={onToggle} aria-expanded={open}>
        <span>{q}</span>
        <span className="faq-icon" aria-hidden="true">+</span>
      </button>
      <div className="faq-answer">
        <p>{a}</p>
      </div>
    </div>
  );
}

// Per-stad hero photos (when available). Add more cities as images come in.
const STAD_IMAGES = {
  "rotterdam": "/assets/stad-rotterdam.png",
};

export function StadPage({ stad }) {
  const isRotterdam = stad.slug === 'rotterdam';
  const CityHero = CITY_HEROES[stad.slug];
  // The luxe stylesheet contains the shared rdam-* + city-specific rules,
  // so load it whenever any bespoke city hero is mounted (not just Rotterdam).
  useEffect(() => { if (CityHero) ensureRotterdamLuxeCss(); }, [CityHero]);
  useScrollProgress(isRotterdam);

  const [openFaq, setOpenFaq] = useState(0);

  // ── Hero offerte form state ──────────────────────────────
  const [hf, setHf] = useState({ naam: "", telefoon: "", postcode: "", ongedierte: "" });
  const [hfSent, setHfSent] = useState(false);
  const updHf = k => e => setHf(f => ({ ...f, [k]: e.target.value }));
  const submitHero = (e) => { e.preventDefault(); setHfSent(true); };

  const faqs = [
    {
      q: `Hoe snel kunnen jullie bij mij in ${stad.naam} zijn?`,
      a: `Gemiddeld zijn wij binnen 24 uur bij u ter plaatse in ${stad.naam}. Bij spoed streven we naar nog snellere respons. Onze specialisten zijn 7 dagen per week actief in en rond ${stad.naam}.`,
    },
    {
      q: `Wat kost ongediertebestrijding in ${stad.naam}?`,
      a: `De prijs hangt af van de plaag, de omvang en de bereikbaarheid. Wij geven altijd telefonisch een eerlijke prijsindicatie vóór de afspraak, geen verrassingen achteraf. Bel ons voor een vrijblijvende prijsindicatie op 085 500 50 50.`,
    },
    {
      q: `Werken jullie ook in het weekend in ${stad.naam}?`,
      a: "Ja, wij zijn 7 dagen per week actief, van maandag t/m zondag van 07:00 tot 22:00. Spoeddiensten zijn 24/7 beschikbaar, ook 's nachts.",
    },
    {
      q: "Is de bestrijding schadelijk voor kinderen of huisdieren?",
      a: "Wij werken uitsluitend met CTGB-gecertificeerde middelen. Na de behandeling geven wij heldere instructies wanneer uw woning weer veilig is voor kinderen en huisdieren.",
    },
    {
      q: "Wat als het wespennest terugkeert?",
      a: "Op wespennesten geven wij 100% seizoensgarantie: keert het nest binnen het seizoen terug, dan behandelen wij het kosteloos opnieuw. Bij andere ongediertesoorten werken wij met vakkundige nacontrole, komt het probleem terug, dan bekijken wij samen de oorzaak en bespreken we een vervolgaanpak.",
    },
    {
      q: "Hoe herken ik een wespennest vs bijennest?",
      a: "Wespen hebben een smallere taille, glad lichaam en zwart-gele strepen. Bijen zijn ronder en behaard. Twijfelt u? Bel ons voor gratis telefonisch advies, wij helpen u onderscheid maken.",
    },
    {
      q: "Mogen huurders ook ABC Ongediertebestrijding inschakelen?",
      a: `Ja, zowel huurders als verhuurders kunnen ons inschakelen. Wij werken ook samen met woningcorporaties in ${stad.naam}.`,
    },
    {
      q: `Welke wijken in ${stad.naam} bedienen jullie?`,
      a: `Wij bedienen alle wijken in ${stad.naam}: ${(STAD_WIJKEN[stad.slug] || []).join(', ') || 'alle wijken'}, plus omliggende gemeenten in ${stad.provincie}.`,
    },
  ];

  // FAQPage schema for rich snippets + Speakable for voice search.
  // 2026-05-29: ook localFaqs uit CITY_CONTENT meenemen zodat de stad-specifieke
  // Q&A (over Crooswijk, werfkelders Utrecht, bedwantsen Pijp etc.) ook rich
  // snippet opportunities krijgen in Google. Verdubbelt het aantal FAQs in
  // schema voor INDEXABLE_CITIES van 8 naar 11.
  const cityLocalFaqs = (CITY_CONTENT[stad.slug]?.localFaqs) || [];
  const allFaqsForSchema = [...faqs, ...cityLocalFaqs];
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allFaqsForSchema.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".faq-question", ".faq-answer"],
    },
  };

  useSEO({
    title: `Ongediertebestrijding ${stad.naam}`,
    description: `Ongediertebestrijding in ${stad.naam}? ABC is dagelijks actief, binnen 24 uur op locatie, vaste prijs vooraf. Spoeddienst 24/7. Werkt met gecertificeerde partners.`,
    path: `/werkgebied/${stad.slug}`,
    jsonLd: [
      buildStadSchema(stad),
      faqSchema,
      buildBreadcrumb([
        { name: "Home", url: "/" },
        { name: "Werkgebied", url: "/werkgebied" },
        { name: stad.naam, url: `/werkgebied/${stad.slug}` },
      ]),
    ],
  });

  // ── Count-up animation on trust badges when in viewport ──
  useEffect(() => {
    const els = document.querySelectorAll('.tc-n[data-counter]');
    const formatNl = (val, decimals) =>
      decimals > 0 ? val.toFixed(decimals).replace('.', ',') : Math.round(val).toString();
    const animate = (el) => {
      const target = parseFloat(el.dataset.counter);
      const decimals = parseInt(el.dataset.decimals || '0', 10);
      const prefix = el.dataset.prefix || '';
      const suffix = el.dataset.suffix || '';
      const duration = 1100;
      const start = performance.now();
      const step = (now) => {
        const t = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
        el.textContent = prefix + formatNl(target * eased, decimals) + suffix;
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting && !e.target.dataset.done) {
          e.target.dataset.done = '1';
          animate(e.target);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.4 });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [stad.slug]);

  const userPc = new URLSearchParams(window.location.search).get('pc') || '';
  const formattedPc = userPc.length >= 5 ? userPc.slice(0, 4) + ' ' + userPc.slice(4, 6) : userPc;

  const wijken = STAD_WIJKEN[stad.slug] || [];
  const omliggende = nearbyCities(stad);

  // Live Google reviews (3) — same 3 on every stad page; locality from review text
  const [cityReviews, setCityReviews] = useState(() => {
    const palette = ["#FF6B2B", "#1A2B4A", "#22C55E", "#8B5CF6", "#F59E0B", "#EF4444"];
    const pool = (GOOGLE_REVIEW_FALLBACK || GOOGLE_REVIEWS_FALLBACK || []).slice(0, 3);
    return pool.map((r, i) => ({
      name: r.name || r.author || "Klant",
      text: r.text || "",
      stars: r.stars || r.rating || 5,
      date: r.time || r.date || "",
      locality: r.locality || null,
      avBg: palette[i % palette.length],
      initial: (r.name || "K").charAt(0).toUpperCase(),
    }));
  });

  // Async: load live Google reviews if available, take first 3
  useEffect(() => {
    if (!getGoogleReviews) return;
    let mounted = true;
    getGoogleReviews().then(reviews => {
      if (!mounted || !reviews || !reviews.length) return;
      const palette = ["#FF6B2B", "#1A2B4A", "#22C55E", "#8B5CF6", "#F59E0B", "#EF4444"];
      const picked = reviews.slice(0, 3).map((r, i) => ({
        name: r.name || "Klant",
        text: r.text || "",
        stars: r.stars || 5,
        date: r.time || "",
        locality: r.locality || null,
        avBg: palette[i % palette.length],
        initial: (r.name || "K").charAt(0).toUpperCase(),
      }));
      setCityReviews(picked);
    });
    return () => { mounted = false; };
  }, [stad.slug]);

  // Live Google aggregaat (rating + totaal). Fallback uit data.js zodat de
  // prerender direct waarden toont; client-side wordt opgewaardeerd zodra de
  // Cloudflare Worker een verse fetch teruggeeft. Zelfde patroon als TopBar.
  const [agg, setAgg] = useState({ rating: REVIEWS.rating, total: REVIEWS.total });
  useEffect(() => {
    let alive = true;
    getGoogleAggregate().then(data => {
      if (!alive || !data) return;
      if (data.rating !== agg.rating || data.total !== agg.total) {
        setAgg({ rating: data.rating, total: data.total });
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const ratingStr = String(agg.rating).replace('.', ',');

  return (
    <div className="page-anim">
      {CityHero ? <CityHero /> : (
      <section className="page-hero">
        {/* ─── HERO ─────────────────────────────────────────────── */}
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="breadcrumb">
            <a href={url.home()}>Home</a><span className="sep">/</span>
            <a href={url.werkgebied()}>Werkgebied</a><span className="sep">/</span>
            <span style={{ color: 'rgba(255,255,255,.9)' }}>{stad.naam}</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 56, alignItems: 'center' }} className="stad-hero">
            <div>
              <div className="urgency-badge">
                <span className="ub-pulse"></span> SPOED BESCHIKBAAR, ook vandaag nog geholpen
              </div>
              <h1 style={{ fontSize: 'clamp(1.7rem,5vw,3.6rem)', fontWeight: 900, color: 'white', lineHeight: 1.1, margin: '12px 0 16px', wordBreak: 'normal', overflowWrap: 'break-word' }}>
                Ongediertebestrijding<br className="hide-mobile"/>
                {' '}in <span style={{ color: 'var(--orange)' }}>{stad.naam}</span>
              </h1>
              <p style={{ color: 'rgba(255,255,255,.78)', fontSize: '1.05rem', maxWidth: '54ch', marginBottom: 26 }}>
                {uniqueStadIntro(stad)}
              </p>

              {formattedPc && (
                <div className="pc-confirm">
                  <span style={{ color: 'var(--orange)' }}>●</span>
                  Postcode <strong style={{ color: 'white' }}>{formattedPc}</strong> valt binnen ons werkgebied
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 }}>
                <a href="tel:0855005050" className="btn-bel-lg">
                  <IconPhone width="16" height="16" /> 085 500 50 50
                </a>
                <a href={url.offerte()} className="btn-form-lg" style={{ borderColor: 'var(--orange)' }}>
                  Gratis offerte voor {stad.naam}
                </a>
              </div>

              {/* Klikbare review-link: opent het Google Business-profiel
                  zodat bezoekers alle reviews kunnen lezen of zelf een
                  review kunnen achterlaten. */}
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${ratingStr} sterren op ${agg.total} Google reviews, bekijk op Google`}
                title="Bekijk al onze Google reviews"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  color: 'rgba(255,255,255,.78)', fontSize: '.9rem',
                  textDecoration: 'none',
                  padding: '6px 10px',
                  margin: '-6px -10px',
                  borderRadius: 8,
                  transition: 'background .2s ease, color .2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.06)'; e.currentTarget.style.color = 'rgba(255,255,255,.95)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,.78)'; }}
              >
                <span style={{ color: '#FBBF24' }}>★★★★★</span>
                <strong style={{ color: 'white' }}>{ratingStr}</strong>
                <span>uit {agg.total} Google reviews</span>
                <span aria-hidden="true" style={{ color: 'rgba(251,191,36,.85)', fontSize: '.82rem', fontWeight: 600 }}>→</span>
              </a>
            </div>

            <RotterdamForm variant="hero" cityName={stad.naam} />
          </div>

          {/* Premium trust panel, same on every city page */}
          <TrustLuxe cityName={stad.naam} />
        </div>
      </section>
      )}

      {/* ─── DIENSTEN GRID ────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="sh">
            <div className="section-tag">Onze diensten in {stad.naam}</div>
            <h2>Professionele bestrijding van <em>alle soorten ongedierte</em></h2>
          </div>
          {/* Editorial card-grid — herbruikt PlaagCard, links naar
              combo-pagina /{stad}/{plaag}bestrijding. */}
          <div className="adc-grid">
            {PESTS.map(p => (
              <PlaagCard
                key={p.slug}
                plaag={p}
                href={url.stadPlaag(stad.slug, p.slug)}
                titleStad={stad.naam}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ─── ROTTERDAM SIGNATURE, kat & muis (uitgezet) ─── */}
      {false && isRotterdam && <RotterdamChase />}

      {/* ─── ROTTERDAM-SPECIFIC EXTRAS (only shown when stad.slug === "rotterdam") ─── */}
      {RotterdamExtras && <RotterdamExtras stad={stad} />}
      {/* Premium wijken-grid met severity bars per wijk — ÉÉN consistent
          component voor alle 117 steden. StadWijkenLuxe lost zelf data-
          bron op: RICH_WIJKEN (Rotterdam) → DEN_HAAG_WIJKEN → CITY_CONTENT
          → auto-generated. De oude CityWijken/DenHaagWijken zijn verwijderd
          uit hun extras-componenten om duplicates te voorkomen. */}
      <StadWijkenLuxe stad={stad} />

      {/* ─── DEN HAAG-SPECIFIC EXTRAS (only shown when stad.slug === "den-haag") ─── */}
      {DenHaagExtras && <DenHaagExtras stad={stad} />}

      {/* ─── OTHER 10 ZUID-HOLLAND CITIES (Leiden, Delft, Dordrecht, etc.) ─── */}
      {CityExtras && <CityExtras stad={stad} />}

      {/* ─── SPOEDSERVICE BANNER ─────────────────────────────── */}
      <section className="spoed-banner">
        <div className="container">
          <div className="spoed-content">
            <div>
              <div className="spoed-tag">⚡ SPOEDSERVICE {stad.naam.toUpperCase()}</div>
              <h2>Last van ongedierte? Wij zijn er vandaag nog.</h2>
              <p>24/7 bereikbaar · Binnen 24 uur ter plaatse · Ook in het weekend</p>
            </div>
            <a href="tel:0855005050" className="btn-white">
              <IconPhone width="18" height="18" /> 085 500 50 50
            </a>
          </div>
        </div>
      </section>

      {/* ─── WERKWIJZE 4 STAPPEN, inspecteur cartoons (zoals home) ─── */}
      <section className="section section-bg">
        <div className="container">
          <div className="sh">
            <div className="section-tag">Onze werkwijze</div>
            <h2>In 4 stappen <em>ongediertevrij</em></h2>
            <p>Eenvoudig, transparant en effectief, zo werken wij elke dag.</p>
          </div>
          <div className="process-grid">
            {[
              { n: 1, t: "Inspectie ter plaatse",   d: "Onze specialist bezoekt je locatie en brengt de situatie volledig in kaart.", img: "/assets/inspecteur-stap-1.png" },
              { n: 2, t: "Aanpak bepalen",     d: "Persoonlijk behandelplan dat precies past bij jouw situatie en het type ongedierte.", img: "/assets/inspecteur-stap-2.png" },
              { n: 3, t: "Duidelijke offerte", d: "Heldere offerte zonder verborgen kosten. Geen verrassingen achteraf.",                img: "/assets/inspecteur-stap-3.png" },
              { n: 4, t: "Direct aan de slag", d: "Na akkoord starten we meteen met een vakkundige, grondige aanpak.",                          img: "/assets/inspecteur-stap-4.png" },
            ].map((s) => (
              <div key={s.n} className="ps">
                <div className="ps-inspector">
                  <picture>
                    <source srcSet={s.img.replace(/\.png$/, '.webp')} type="image/webp" />
                    <img
                      src={s.img}
                      alt={`Inspecteur stap ${s.n}: ${s.t}`}
                      className="ps-inspector-img"
                      loading="lazy"
                      decoding="async"
                      width="220"
                      height="260"
                    />
                  </picture>
                </div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── LOKALE SEO CONTENT BLOK ─────────────────────────── */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 56, alignItems: 'center' }} className="seo-block">
            <div>
              <div className="section-tag" style={{ marginBottom: 12 }}>{stad.naam}</div>
              <h2 style={{ fontSize: 'clamp(1.8rem,3vw,2.4rem)', fontWeight: 800, color: 'var(--navy)', margin: '0 0 18px', lineHeight: 1.15 }}>
                Ongediertebestrijding in {stad.naam}: <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>snel &amp; vakkundig</em>
              </h2>
              <p style={{ color: 'var(--text)', fontSize: '1rem', lineHeight: 1.7, marginBottom: 14 }}>
                ABC Ongediertebestrijding is dagelijks actief in {stad.naam} en omliggende gemeenten. Onze
                gecertificeerde specialisten kennen de lokale situatie, van oude stadsdelen tot moderne nieuwbouwwijken, 
                en zijn gemiddeld binnen 24 uur bij u ter plaatse.
              </p>
              <p style={{ color: 'var(--text)', fontSize: '1rem', lineHeight: 1.7, marginBottom: 22 }}>
                In {stad.naam} bestrijden wij regelmatig wespen, muizen, ratten, kakkerlakken en bedwantsen in woningen,
                bedrijfspanden en appartementen. Wij werken uitsluitend met CTGB-gecertificeerde bestrijdingsmiddelen.
              </p>
              <ul className="checklist">
                <li><IconCheck /> Dagelijks actief in {stad.naam}</li>
                <li><IconCheck /> Gemiddeld binnen 24 uur op locatie</li>
                <li><IconCheck /> Werkt met gecertificeerde partners</li>
                <li><IconCheck /> Milieuverantwoorde middelen (CTGB)</li>
                <li><IconCheck /> Vakkundige nacontrole na behandeling</li>
                <li><IconCheck /> Betaling ook achteraf mogelijk</li>
              </ul>
            </div>
            <div className="seo-photo seo-photo--filled" style={{ height: 480 }}>
              <picture>
                <source srcSet="/assets/photo-inspecteur-rotterdam.webp" type="image/webp" />
                <img
                  src="/assets/photo-inspecteur-rotterdam.jpg"
                  alt={`ABC bestrijder in beschermende kleding tijdens ongediertebestrijding in ${stad.naam}`}
                  loading="lazy"
                  decoding="async"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'right center', display: 'block' }}
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TRUST & KWALITEIT ───────────────────────────────── */}
      <section className="section trust-garanties">
        <div className="container">
          <div className="sh" style={{ marginBottom: 40 }}>
            <div className="section-tag" style={{ background: 'rgba(255,107,43,.18)', color: '#FFB088' }}>Vertrouwen &amp; kwaliteit</div>
            <h2 style={{ color: 'white' }}>Waarom klanten in <em>{stad.naam}</em> voor ABC kiezen</h2>
          </div>
          <div className="garanties-grid">
            <div className="garantie-card">
              <div className="g-icon"><IconShield /></div>
              <h3>Seizoensgarantie wespen</h3>
              <p>Wespennesten die binnen het seizoen terugkeren behandelen wij kosteloos opnieuw. 100% garantie, uitsluitend op wespenbestrijding.</p>
            </div>
            <div className="garantie-card">
              <div className="g-icon"><IconBolt /></div>
              <h3>Gecertificeerde partners</h3>
              <p>Wij werken uitsluitend met IPM-gecertificeerde plaagdierspecialisten en CTGB-gecertificeerde bestrijdingsmiddelen.</p>
            </div>
            <div className="garantie-card">
              <div className="g-icon"><IconLeaf /></div>
              <h3>Milieuvriendelijk</h3>
              <p>Wij werken met CTGB-gecertificeerde, veilige middelen. Goed voor mens &amp; milieu.</p>
            </div>
            <div className="garantie-card">
              <div className="g-icon"><IconWallet /></div>
              <h3>Geen verborgen kosten</h3>
              <p>Eerlijke prijzen, vaste prijs vooraf, geen verrassingen achteraf.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── REVIEWS ─────────────────────────────────────────── */}
      <section className="section section-bg">
        <div className="container">
          <div className="reviews-head">
            <div>
              <div className="section-tag">Klanten</div>
              <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 800, color: 'var(--navy)', marginTop: 10 }}>
                Wat klanten <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>over ons zeggen</em>
              </h2>
              <p style={{ color: 'var(--text-muted)', marginTop: 6 }}>{ratingStr} gemiddeld uit {agg.total} Google beoordelingen</p>
            </div>
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="g-score g-score-link"
              aria-label={`${ratingStr} sterren op ${agg.total} Google reviews, bekijk op Google`}
              title="Bekijk al onze Google reviews"
            >
              <div className="g-score-icon">G</div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="g-num">{ratingStr}</span>
                  <span className="g-stars">★★★★★</span>
                </div>
                <div className="g-lbl">{agg.total} Google reviews →</div>
              </div>
            </a>
          </div>

          <div className="reviews-grid">
            {cityReviews.map((r, i) => (
              <div key={i} className="rc">
                <div className="rc-top">
                  <div className="rc-av" style={{ background: r.avBg }}>{r.initial}</div>
                  <div>
                    <div className="rc-name">{r.name}</div>
                    <div className="rc-loc">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="#FBBF24" aria-hidden="true">
                        <path d="M12 2 L14.5 9 L22 9 L16 13.5 L18.5 20.5 L12 16.5 L5.5 20.5 L8 13.5 L2 9 L9.5 9 Z"/>
                      </svg>
                      &nbsp;Google review
                    </div>
                  </div>
                </div>
                <div className="rc-stars">{'★'.repeat(r.stars || 5)}</div>
                <p className="rc-text">"{r.text}"</p>
                <div className="rc-date">{r.date}{r.locality ? ` · ${r.locality}` : ''} · via Google</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <a href={url.reviews()} className="btn-meer" style={{ display: 'inline-flex' }}>
              Bekijk alle {agg.total} reviews →
            </a>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─────────────────────────────────────────────── */}
      <section className="section faq-section">
        <div className="container" style={{ maxWidth: 860 }}>
          <div className="sh">
            <div className="section-tag">FAQ</div>
            <h2>Veelgestelde vragen over <em>ongediertebestrijding in {stad.naam}</em></h2>
          </div>
          <div className="faq-list">
            {faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} open={openFaq === i}
                       onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── WERKGEBIED / WIJKEN ─────────────────────────────── */}
      <section className="section section-bg">
        <div className="container">
          <div className="sh">
            <div className="section-tag">Werkgebied</div>
            <h2>{stad.naam} en <em>omstreken</em></h2>
            <p>ABC Ongediertebestrijding is actief in {stad.naam} en alle omliggende gemeenten.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'start' }} className="werkgebied-grid">
            <div>
              {wijken.length > 0 && (
                <>
                  <h3 style={{ fontSize: '1.05rem', color: 'var(--navy)', fontWeight: 700, marginBottom: 14 }}>Wijken in {stad.naam}</h3>
                  <div className="steden-grid" style={{ marginBottom: 28 }}>
                    {wijken.map(w => (
                      <span key={w} className="wijk-pill"><IconPin width="11" height="11" />&nbsp;{w}</span>
                    ))}
                  </div>
                </>
              )}
              {omliggende.length > 0 && (
                <>
                  <h3 style={{ fontSize: '1.05rem', color: 'var(--navy)', fontWeight: 700, marginBottom: 14 }}>Omliggende gemeenten</h3>
                  <div className="steden-grid">
                    {omliggende.map(s => (
                      <a key={s.slug} href={url.stad(s.slug)} className="wijk-link">
                        <IconPin width="11" height="11" />&nbsp;{s.naam}
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="map-embed">
              <iframe
                title={`Google Maps, werkgebied ${stad.naam}`}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(stad.naam + ', Nederland')}&t=&z=12&ie=UTF8&iwloc=&output=embed`}
                width="100%"
                height="380"
                style={{ border: 0, borderRadius: 16, display: 'block' }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SECOND CTA ──────────────────────────────────────── */}
      <section className="cta-block">
        <div className="container">
          <div className="cta-block-inner">
            <h2>Klaar om uw ongedierteprobleem op te lossen?</h2>
            <p>
              Bel ons direct of vraag een gratis offerte aan. Wij zijn bereikbaar van ma t/m zo, 07:00-22:00.
              Spoed? Ook 's nachts bereikbaar.
            </p>
            <div className="cta-block-buttons">
              <a href="tel:0855005050" className="cta-btn cta-btn-primary">
                <IconPhone width="18" height="18" /> 085 500 50 50
              </a>
              <a href={url.offerte()} className="cta-btn cta-btn-secondary">
                Offerte aanvragen
              </a>
            </div>
            <div className="cta-block-trust">
              ✓ Gratis advies &nbsp;·&nbsp; ✓ Geen verborgen kosten &nbsp;·&nbsp; ✓ Gecertificeerde partners
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

